import { Link } from "react-router-dom";
import { motion } from "framer-motion";

const fadeUp = {
  hidden: { opacity: 0, y: 24 },
  visible: (i = 0) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.9, ease: [0.22, 1, 0.36, 1], delay: i * 0.12 },
  }),
};

export default function Landing() {
  const portrait =
    "https://static.wixstatic.com/media/af9b25_a801814eed3a4f1da0401c2af5aefcf5~mv2.jpeg/v1/fill/w_900,h_1100,al_c,q_85,enc_avif,quality_auto/eclaire_toi_hero.jpeg";

  return (
    <div className="relative min-h-screen overflow-hidden bg-sand-50" data-testid="landing-page">
      {/* Top brand band, clin d'œil au site eclaire-toi.fr */}
      <div className="bg-sand-300/70 backdrop-blur-sm border-b border-sand-300">
        <div className="max-w-7xl mx-auto px-6 md:px-10 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3" data-testid="brand-logo">
            <img
              src="https://static.wixstatic.com/media/af9b25_f36f1ab2bff848758e1745acea6e006c~mv2.png/v1/fill/w_360,h_212,al_c,q_85,enc_avif,quality_auto/eclaire_toi_texte_et_main.png"
              alt="Éclaire-Toi"
              className="h-12 md:h-14 w-auto"
            />
          </Link>
          <Link
            to="/admin"
            className="text-[11px] uppercase tracking-[0.22em] text-sand-600 hover:text-sand-700 transition-colors"
            data-testid="admin-link"
          >
            Espace admin
          </Link>
        </div>
      </div>

      {/* Hero */}
      <main className="relative z-10 mx-auto max-w-6xl px-6 md:px-12 pt-16 md:pt-24 pb-20">
        <div className="grid md:grid-cols-12 gap-12 md:gap-20 items-center">
          <div className="md:col-span-7">
            <motion.p
              variants={fadeUp}
              initial="hidden"
              animate="visible"
              custom={0}
              className="text-[11px] uppercase tracking-[0.4em] text-sand-500 mb-8 flex items-center gap-3"
              data-testid="kicker"
            >
              <span className="h-px w-10 bg-sand-400" />
              Diagnostic · Lumière Intérieure
              <span className="text-sand-400 text-[14px]">✦</span>
            </motion.p>

            <motion.h1
              variants={fadeUp}
              initial="hidden"
              animate="visible"
              custom={1}
              className="font-display text-[2.5rem] sm:text-[3rem] lg:text-[3.6rem] leading-[1.05] text-ink"
              data-testid="hero-title"
            >
              Et si votre fatigue ne venait pas seulement
              <em className="not-italic text-sand-500"> de tout ce que vous faites,</em>
              <br />
              mais aussi de tout ce que vous portez intérieurement&nbsp;?
            </motion.h1>

            <motion.p
              variants={fadeUp}
              initial="hidden"
              animate="visible"
              custom={2}
              className="mt-8 text-lg leading-relaxed text-ink-soft max-w-xl"
              data-testid="hero-subtitle"
            >
              En quelques minutes, mettez des mots sur ce qui pèse,
              sans bruit, sans drame, mais avec lucidité.
            </motion.p>

            <motion.div
              variants={fadeUp}
              initial="hidden"
              animate="visible"
              custom={3}
              className="mt-10 space-y-4 text-base text-ink-soft max-w-xl"
            >
              <p>Vous avancez. Vous tenez. Vous faites face.</p>
              <p className="text-ink-soft/90">
                Vous anticipez, vous vous adaptez, vous prenez sur vous.
                Vous repoussez vos besoins à plus tard. Vous gérez, pour tout le monde.
              </p>
              <p>
                Et puis, au fil du temps, une fatigue s'installe.
                Une fatigue qui n'a pas vraiment de nom, mais qui se reconnaît.
              </p>
              <p className="text-ink">
                Ce diagnostic n'est pas un test. C'est un miroir doux pour
                reconnaître certaines dynamiques qui, sans que vous l'ayez choisi,
                vous éloignent peu à peu de vous-même.
              </p>
            </motion.div>

            <motion.div
              variants={fadeUp}
              initial="hidden"
              animate="visible"
              custom={4}
              className="mt-12 flex flex-col sm:flex-row sm:items-center gap-6"
            >
              <Link
                to="/diagnostic"
                className="group relative inline-flex items-center gap-3 rounded-full bg-sand-500 hover:bg-sand-600 text-white pl-6 pr-2 py-2 shadow-soft transition-all duration-500 hover:shadow-elevated whitespace-nowrap"
                data-testid="start-diagnostic-btn"
              >
                <span className="text-sm font-medium tracking-wide leading-none">Commencer le diagnostic</span>
                <span
                  aria-hidden
                  className="flex h-9 w-9 items-center justify-center rounded-full bg-white/15 transition-all duration-500 group-hover:bg-white group-hover:text-sand-500 group-hover:rotate-[-12deg] text-sm"
                >
                  →
                </span>
              </Link>
              <div className="flex items-center gap-3 text-xs uppercase tracking-[0.22em] text-ink-muted" data-testid="hero-micro">
                <span className="h-px w-6 bg-sand-300" />
                5 min · à votre rythme · échange humain en visio
              </div>
            </motion.div>
          </div>

          {/* Visual */}
          <motion.div
            initial={{ opacity: 0, scale: 0.96 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 1.4, ease: [0.22, 1, 0.36, 1], delay: 0.2 }}
            className="md:col-span-5 relative"
          >
            {/* sparkle accent */}
            <div className="absolute -top-4 -left-4 text-sand-400 text-2xl animate-breath select-none z-10" aria-hidden>✦</div>
            <div className="absolute -bottom-2 right-6 text-sand-300 text-xl select-none z-10" aria-hidden>✦</div>

            <div className="relative aspect-[3/4] w-full overflow-hidden rounded-t-[180px] rounded-b-2xl shadow-elevated">
              <img
                src={portrait}
                alt="Silhouette sereine dans une lumière chaude"
                className="h-full w-full object-cover transition-transform duration-[2s] hover:scale-105"
                data-testid="hero-image"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-sand-500/25 via-transparent to-transparent" />
            </div>
            <div className="mt-6 flex items-center gap-3 pl-2">
              <span className="text-sand-400">✦</span>
              <p className="italic text-ink-soft text-sm">
                pour vous, juste pour un instant
              </p>
            </div>
          </motion.div>
        </div>

        {/* Bordeaux quote band, inspiré directement de eclaire-toi.fr */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.7, duration: 1 }}
          className="relative mt-24 mx-[-1.5rem] md:mx-[-3rem] bg-sand-500 text-white px-8 md:px-16 py-12 md:py-16 overflow-hidden"
          data-testid="quote-band"
        >
          <img
            src="https://static.wixstatic.com/media/af9b25_9126e01e9d29499daefb0e36c0454a0b~mv2.png/v1/crop/x_21,y_25,w_1220,h_572/fill/w_700,h_330,al_c,q_85,enc_avif,quality_auto/eclaire_moi_main.png"
            alt=""
            aria-hidden
            className="hidden md:block absolute -right-8 -bottom-6 w-[380px] opacity-90 select-none pointer-events-none"
          />
          <p className="relative font-display text-2xl md:text-4xl leading-snug max-w-3xl">
            "Explore tes aspirations, illumine ton chemin et rayonne ton authenticité."
          </p>
          <p className="relative mt-5 text-sm md:text-base text-sand-100/90 max-w-xl">
            Deviens la femme qui ose briller pleinement et vivre en harmonie avec ses valeurs.
          </p>
        </motion.div>

        {/* Trust signals */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.9, duration: 0.8 }}
          className="mt-20 grid sm:grid-cols-3 gap-5 max-w-4xl"
        >
          {[
            ["Sans jugement", "Un espace pour reconnaître, pas pour évaluer."],
            ["Confidentiel", "Vos mots restent entre vous et Cindy. Rien d'autre."],
            ["Incarné", "Une lecture humaine, en visio, avec une voix en face."],
          ].map(([title, desc], i) => (
            <div
              key={title}
              className="group rounded-2xl border border-sand-200 bg-white/70 backdrop-blur-sm p-7 hover:border-sand-400 hover:bg-white transition-all duration-500 hover:-translate-y-1 hover:shadow-soft"
              data-testid={`trust-${i}`}
            >
              <div className="flex items-center gap-2 mb-3">
                <span className="text-sand-400 text-sm transition-transform duration-500 group-hover:rotate-180">✦</span>
                <p className="font-display text-xl text-sand-500">{title}</p>
              </div>
              <p className="text-sm text-ink-soft leading-relaxed">{desc}</p>
            </div>
          ))}
        </motion.div>
      </main>

      <footer className="relative z-10 border-t border-sand-200 mt-10 py-8 px-6 md:px-12 flex flex-col md:flex-row justify-between items-center gap-2 text-xs text-ink-muted">
        <p>© {new Date().getFullYear()} Éclaire-Toi · Lumière Intérieure</p>
        <p className="italic flex items-center gap-2">
          <span className="text-sand-400">✦</span>
          Conçu avec douceur par Cindy
        </p>
      </footer>
    </div>
  );
}
